import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Shield, 
  Users, 
  Trophy,
  MapPin,
  Crown,
  Star,
  Settings,
  UserPlus,
  UserMinus,
  Megaphone,
  TrendingUp,
  Calendar,
  Award,
  Clock,
  Send,
  MessageCircle,
  Swords
} from "lucide-react";

const LEVEL_COLORS = {
  1: "bg-amber-700",
  2: "bg-gray-400",
  3: "bg-yellow-500",
  4: "bg-cyan-400",
  5: "bg-purple-500",
};

const LEVEL_NAMES = {
  1: "Bronz",
  2: "Gümüş",
  3: "Altın",
  4: "Platin",
  5: "Elmas"
};

const DEFAULT_LOGOS = {
  // Kuşlar
  eagle: "🦅",
  canary: "🐤",
  falcon: "🦅",
  owl: "🦉",
  phoenix: "🔥",
  // Kedigiller
  lion: "🦁",
  tiger: "🐯",
  panther: "🐆",
  leopard: "🐆",
  // Diğer Hayvanlar
  wolf: "🐺",
  bear: "🐻",
  bull: "🐂",
  horse: "🐴",
  crocodile: "🐊",
  shark: "🦈",
  dragon: "🐉",
  snake: "🐍",
  scorpion: "🦂",
  bee: "🐝",
  // Semboller
  lightning: "⚡",
  star: "⭐",
  fire: "🔥",
  trophy: "🏆",
  shield: "🛡️",
  sword: "⚔️",
  crown: "👑",
  diamond: "💎",
  rocket: "🚀",
  anchor: "⚓",
};

const ROLE_NAMES = {
  president: "Başkan",
  manager: "Yönetici",
  captain: "Kaptan",
  player: "Oyuncu"
};

const ROLE_ICONS = {
  president: Crown,
  manager: Star,
  captain: Shield,
  player: Users
};

const ClubDetailPage = ({ user }) => {
  const navigate = useNavigate();
  const { clubId } = useParams();
  const [club, setClub] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState("");
  const [myApplication, setMyApplication] = useState(null);

  useEffect(() => {
    fetchClub();
    fetchMyApplication();
  }, [clubId]);

  const fetchClub = async () => {
    try {
      const response = await axios.get(`${API}/clubs/${clubId}`);
      setClub(response.data);
      
      // Fetch announcements if member
      if (response.data.members?.some(m => m.userId === user?.id)) {
        try {
          const announcementsRes = await axios.get(`${API}/clubs/${clubId}/announcements`);
          setAnnouncements(announcementsRes.data);
        } catch (e) {
          // Ignore error
        }
      }
    } catch (error) {
      toast.error("Kulüp bulunamadı");
      navigate("/clubs");
    } finally {
      setLoading(false);
    }
  };

  const fetchMyApplication = async () => {
    try {
      const response = await axios.get(`${API}/clubs/my-applications`);
      const pendingApp = response.data.find(app => app.clubId === clubId && app.status === "pending");
      setMyApplication(pendingApp || null);
    } catch (error) {
      // Ignore
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      const response = await axios.post(`${API}/clubs/${clubId}/join`, {
        message: applicationMessage
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setShowApplyDialog(false);
        setApplicationMessage("");
        fetchMyApplication();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Başvuru gönderilemedi");
    } finally {
      setJoining(false);
    }
  };

  const handleCancelApplication = async () => {
    if (!myApplication) return;
    try {
      await axios.delete(`${API}/clubs/applications/${myApplication.id}`);
      toast.success("Başvuru iptal edildi");
      setMyApplication(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "İptal başarısız");
    }
  };

  const handleLeave = async () => {
    if (!window.confirm("Kulüpten ayrılmak istediğinize emin misiniz?")) return;
    
    setLeaving(true);
    try {
      const response = await axios.post(`${API}/clubs/${clubId}/leave`);
      if (response.data.success) {
        toast.success(response.data.message);
        navigate("/clubs");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ayrılma başarısız");
    } finally {
      setLeaving(false);
    }
  };

  const getLogo = () => {
    if (!club) return "🏟️";
    if (club.logoType === "custom" && club.logo) {
      return <img src={club.logo} alt={club.name} className="w-full h-full object-cover rounded-2xl" />;
    }
    return <span className="text-6xl">{DEFAULT_LOGOS[club.logo] || "🏟️"}</span>;
  };

  const isMember = club?.members?.some(m => m.userId === user?.id);
  const myRole = club?.members?.find(m => m.userId === user?.id)?.role;
  const isAdmin = myRole === "president" || myRole === "manager";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!club) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Header */}
      <div 
        className="text-white"
        style={{ background: `linear-gradient(135deg, ${club.primaryColor}, ${club.secondaryColor || club.primaryColor}dd)` }}
      >
        <div className="container mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate("/clubs")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kulüplere Dön
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            <div 
              className="w-24 h-24 rounded-2xl flex items-center justify-center bg-white/20 backdrop-blur-sm"
            >
              {getLogo()}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{club.name}</h1>
                <Badge className={`${LEVEL_COLORS[club.level]} text-white`}>
                  {LEVEL_NAMES[club.level]}
                </Badge>
              </div>
              
              {club.description && (
                <p className="text-white/80 mb-3">{club.description}</p>
              )}
              
              <div className="flex flex-wrap gap-4 text-sm text-white/90">
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {club.members?.length || 0}/{club.memberLimit} Üye
                </span>
                {club.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {club.city}{club.district ? `, ${club.district}` : ""}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Trophy className="h-4 w-4" />
                  {club.stats?.wins || 0} Galibiyet
                </span>
              </div>
            </div>
            
            <div className="flex gap-2">
              {!isMember && club.isRecruiting && !myApplication && (
                <Button 
                  className="bg-white text-gray-900 hover:bg-gray-100"
                  onClick={() => setShowApplyDialog(true)}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Başvur
                </Button>
              )}
              
              {!isMember && myApplication && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-yellow-500 text-white px-3 py-1">
                    <Clock className="h-3 w-3 mr-1" />
                    Başvuru Beklemede
                  </Badge>
                  <Button 
                    variant="outline"
                    size="sm"
                    className="border-white/30 text-white hover:bg-white/20"
                    onClick={handleCancelApplication}
                  >
                    İptal
                  </Button>
                </div>
              )}
              
              {isMember && myRole !== "president" && (
                <Button 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/20"
                  onClick={handleLeave}
                  disabled={leaving}
                >
                  <UserMinus className="h-4 w-4 mr-2" />
                  Ayrıl
                </Button>
              )}
              
              {/* Kulüp Chat ve Maç Eşleşme - Sadece Üyeler */}
              {isMember && (
                <>
                  <Button 
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/20"
                    onClick={() => navigate('/club-chat')}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Kulüp Sohbet
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/20"
                    onClick={() => navigate('/matches')}
                  >
                    <Swords className="h-4 w-4 mr-2" />
                    Maç Eşleşme
                  </Button>
                </>
              )}
              
              {isAdmin && (
                <Button 
                  variant="outline"
                  size="sm"
                  className="border-white/30 text-white hover:bg-white/20 text-xs sm:text-sm px-2 sm:px-4"
                  onClick={() => navigate(`/clubs/${clubId}/manage`)}
                >
                  <Settings className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Yönet</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="members" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="members">
              <Users className="h-4 w-4 mr-2" />
              Üyeler
            </TabsTrigger>
            <TabsTrigger value="stats">
              <TrendingUp className="h-4 w-4 mr-2" />
              İstatistikler
            </TabsTrigger>
            {isMember && (
              <TabsTrigger value="announcements">
                <Megaphone className="h-4 w-4 mr-2" />
                Duyurular
              </TabsTrigger>
            )}
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Kulüp Kadrosu
                </CardTitle>
                <CardDescription>
                  {club.members?.length || 0} üye
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {club.members?.sort((a, b) => {
                    const order = { president: 0, manager: 1, captain: 2, player: 3 };
                    return order[a.role] - order[b.role];
                  }).map((member) => {
                    const RoleIcon = ROLE_ICONS[member.role];
                    return (
                      <div 
                        key={member.userId}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/profile/${member.userId}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                            {member.userImage ? (
                              <img src={member.userImage} alt={member.userName} className="w-full h-full object-cover" />
                            ) : (
                              <Users className="h-6 w-6 text-emerald-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {member.userName}
                              {member.badge && (
                                <Badge variant="outline" className="text-xs">
                                  {member.badge === "star" && "⭐"}
                                  {member.badge === "elite" && "💎"}
                                  {member.badge === "mvp" && "🏆"}
                                </Badge>
                              )}
                            </p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <RoleIcon className="h-3 w-3" />
                              {ROLE_NAMES[member.role]}
                            </p>
                          </div>
                        </div>
                        
                        {member.role === "president" && (
                          <Crown className="h-5 w-5 text-yellow-500" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6 text-center">
                  <Trophy className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
                  <p className="text-3xl font-bold">{club.stats?.wins || 0}</p>
                  <p className="text-sm text-gray-500">Galibiyet</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <Calendar className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                  <p className="text-3xl font-bold">{club.stats?.draws || 0}</p>
                  <p className="text-sm text-gray-500">Beraberlik</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <TrendingUp className="h-8 w-8 mx-auto text-red-500 mb-2" />
                  <p className="text-3xl font-bold">{club.stats?.losses || 0}</p>
                  <p className="text-sm text-gray-500">Mağlubiyet</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <Award className="h-8 w-8 mx-auto text-yellow-500 mb-2" />
                  <p className="text-3xl font-bold">{club.stats?.points || 0}</p>
                  <p className="text-sm text-gray-500">Puan</p>
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Gol İstatistikleri</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-8">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-emerald-600">{club.stats?.goalsScored || 0}</p>
                    <p className="text-gray-500">Atılan Gol</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-red-600">{club.stats?.goalsConceded || 0}</p>
                    <p className="text-gray-500">Yenilen Gol</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          {isMember && (
            <TabsContent value="announcements">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Megaphone className="h-5 w-5" />
                    Duyurular
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {announcements.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Henüz duyuru yok</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {announcements.map((announcement) => (
                        <div key={announcement.id} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium">{announcement.authorName}</span>
                            <span className="text-sm text-gray-500">
                              {new Date(announcement.createdAt).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                          <p className="text-gray-700">{announcement.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Başvuru Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Kulübe Başvur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{getLogo()}</div>
                <div>
                  <p className="font-semibold">{club?.name}</p>
                  <p className="text-sm text-gray-600">
                    {club?.members?.length || 0} / {club?.memberLimit} üye
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Başvuru Mesajı (Opsiyonel)</Label>
              <Textarea
                placeholder="Kendinizi tanıtın, neden bu kulübe katılmak istiyorsunuz?"
                value={applicationMessage}
                onChange={(e) => setApplicationMessage(e.target.value)}
                rows={4}
              />
            </div>
            
            <p className="text-sm text-gray-500">
              Başvurunuz kulüp yöneticileri tarafından değerlendirilecektir.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleJoin} disabled={joining}>
              {joining ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Gönderiliyor...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Başvuruyu Gönder
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClubDetailPage;
